import React from "react";
import { useTranslation } from "react-i18next";
import { MediaQueryCounts } from "../common/ObjectTypes";
import { ReviewStatusFilter } from "../common/QueryTypes";
import { deriveKpis } from "./mediaQueryBuilder";

type ReviewQueueStatsProps = {
  counts: MediaQueryCounts | null;
  /** The Review Status filter that produced `counts`, or null when none was applied. */
  reviewStatus: ReviewStatusFilter | null;
};

type Tile = {
  label: string;
  value: number;
  sub?: string;
};

/**
 * KPI stat tiles for the review-queue results page: total assets, AI processed
 * (with queued count), awaiting review, and review complete. Figures are
 * derived from the server counts payload; renders nothing until counts load.
 *
 * Deliberately compact (AKL-753 follow-up): these sit above the grid on every
 * visit, so the space they take is space the photos do not get. Tightened
 * padding, type scale and leading rather than dropping any of the four figures —
 * the numbers all earn their place, the whitespace did not.
 */
const ReviewQueueStats: React.FC<ReviewQueueStatsProps> = ({
  counts,
  reviewStatus,
}) => {
  const { t } = useTranslation();
  const kpis = deriveKpis(counts, reviewStatus);
  if (!kpis) return null;

  const tiles: Tile[] = [
    { label: t("Total Assets"), value: kpis.totalAssets },
    {
      label: t("AI Processed"),
      value: kpis.aiProcessed,
      sub: t("{{pct}}% · {{queued}} queued", {
        pct: kpis.processedPct,
        queued: kpis.queued.toLocaleString(),
      }),
    },
    // Both review percentages are a share of every photo the filters select,
    // the Total Assets figure, not of processed photos only (AKL-993).
    {
      label: t("Awaiting Review"),
      value: kpis.awaiting,
      sub: t("{{pct}}% of all matching photos", { pct: kpis.awaitingPct }),
    },
    {
      label: t("Review Complete"),
      value: kpis.complete,
      sub: t("{{pct}}% of all matching photos", { pct: kpis.completePct }),
    },
  ];

  return (
    <div
      className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3"
      data-testid="review-queue-stats"
    >
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="bg-off-white-dark dark:bg-storm-blue-card rounded px-3 py-2 text-left transition duration-300"
        >
          <div className="text-[11px] uppercase leading-tight tracking-wide text-off-black-light dark:text-primary-off-white">
            {tile.label}
          </div>
          <div className="text-2xl font-bold leading-tight text-storm-blue dark:text-sky-blue-light">
            {tile.value.toLocaleString()}
          </div>
          {tile.sub && (
            <div className="text-xs leading-tight text-off-black-light dark:text-primary-off-white mt-0.5">
              {tile.sub}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ReviewQueueStats;
